import { FontColorEnum, FormField, FormHint, Switch, SwitchColorEnum } from '@acceso/design-system';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type States<Active, Inactive> = {
  active: Active;
  inactive: Inactive;
};

type TInputSwitchProps<T extends FieldValues, Active, Inactive> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  required?: boolean;
  disabled?: boolean;
  states?: States<Active, Inactive> | States<true, false>;
  label?: string;
  color?: SwitchColorEnum;
};

export function InputSwitch<T extends FieldValues, Active, Inactive>({
  form,
  name,
  required,
  disabled,
  states = {
    active: true,
    inactive: false,
  },
  label,
  color = SwitchColorEnum.SUCCESS,
}: TInputSwitchProps<T, Active, Inactive>) {
  const { t } = useTranslation();
  const { control } = form;

  return (
    <FormField className="flex items-end">
      <Controller
        disabled={disabled}
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const defaultLabel = value === states.active ? t('Activo') : t('Inactivo');

          return (
            <>
              <Switch
                startLabel={label ?? defaultLabel}
                checked={value === states.active}
                onChange={checked => {
                  onChange(checked ? states.active : states.inactive);
                }}
                color={color}
              />

              {error && (
                <FormHint color={FontColorEnum.Error20} type="error">
                  {error.message}
                </FormHint>
              )}
            </>
          );
        }}
      />
    </FormField>
  );
}
